import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
	ComponentController,
	MessagingService,
	filterNil,
	filterNull,
	localeDateToDateTime,
} from '@lycoris-nubila/ngx-lycoris';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { uniq } from 'lodash-es';
import { SelectItem } from 'primeng/api';
import { Observable, combineLatest, of } from 'rxjs';
import {
	debounceTime,
	filter,
	map,
	shareReplay,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';

import { SortDirection } from 'infrastructure/common/model/api.sort-direction';
import { CoacheeProfileMeetingsFilterForm } from 'presentation/page/profile/coachee-profile/coachee-profile-meetings/coachee-profile-meetings.model';
import {
	CoacheeProfileMeetingsState,
	CoacheeProfileMeetingsStore,
} from 'presentation/page/profile/coachee-profile/coachee-profile-meetings/coachee-profile-meetings.store';
import { CoacheeProfileController } from 'presentation/page/profile/coachee-profile/coachee-profile.controller';
import { TASK_STATUSES, TaskStatus } from 'task/model/task.status';
import { TaskApplicationPort } from 'task/port/task-application.port';
import { ITask } from 'task/task';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserTaskExportTaskCommandHandler } from 'user/user-task-export-task-command.handler';
import { UserTaskExportTaskQueryHandler } from 'user/user-task-export-task-query.handler';

import { filterError } from '../../../../../app.util';

@Injectable()
@UntilDestroy()
export class CoacheeProfileMeetingsController extends ComponentController<CoacheeProfileMeetingsState> {
	public meetings$: Observable<ITask[]>;
	public dateSortCssIconClass$: Observable<string>;
	public taskStatusOptions$: Observable<SelectItem[]>;
	public i18n =
		'presentation.page.profile.coachee-profile.coachee-profile-meetings';

	public filterForm: FormGroup<CoacheeProfileMeetingsFilterForm>;
	public coacheeId$: Observable<string>;
	public isExporting$: Observable<boolean>;
	public isHandsetLandscape$: Observable<boolean>;

	constructor(
		store: CoacheeProfileMeetingsStore,
		private _http: HttpClient,
		private _messagingService: MessagingService,
		private _breakpointObserver: BreakpointObserver,
		private _coacheeProfileController: CoacheeProfileController,
		private _taskApplicationPort: TaskApplicationPort,
		private _userApplicationPort: UserApplicationPort,
		private _translateService: TranslateService,
		private _userTaskExportTaskQueryHandler: UserTaskExportTaskQueryHandler,
		private _userTaskExportTaskCommandHandler: UserTaskExportTaskCommandHandler
	) {
		super(store);

		this.filterForm = new FormGroup<CoacheeProfileMeetingsFilterForm>({
			dateSort: new FormControl<SortDirection>('DESC'),
			statuses: new FormControl<TaskStatus[]>(),
			description: new FormControl<string>(''),
			date: new FormControl<Date>(),
		});

		this._initObservables();
		this._initListeners();
	}

	public loadNextPage() {
		this.store.update((state) => ({
			meetingsOffset: (state.meetingsOffset ?? 0) + state.meetingsChunkSize,
		}));
	}

	private _initObservables() {
		this.isHandsetLandscape$ = this._breakpointObserver
			.observe(Breakpoints.HandsetLandscape)
			.pipe(
				map((result) => result.matches),
				shareReplay({ refCount: true, bufferSize: 1 })
			);

		this.isExporting$ = this.select('isExporting').pipe(
			filterNull(),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.coacheeId$ = this._coacheeProfileController.coachee$.pipe(
			map((coachee) => coachee?.id),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.meetings$ = this.select('meetingsId').pipe(
			switchMap((meetingsId) =>
				this._taskApplicationPort.observeUserCoachMeetings(meetingsId)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.taskStatusOptions$ = combineLatest(
			TASK_STATUSES.map((status) =>
				this._translateService
					.stream(this.i18n + '.task.statuses.' + status)
					.pipe(map((label) => ({ value: status, label } as SelectItem)))
			)
		).pipe(shareReplay({ bufferSize: 1, refCount: true }));

		this.dateSortCssIconClass$ = this.filterForm.value$.pipe(
			map((value) =>
				value.dateSort === 'DESC'
					? 'fak fa-str-arrow-down-wide-short'
					: 'fak fa-str-arrow-up-short-wide'
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public toggleDateSort() {
		this.filterForm.patchValue({
			dateSort: this.filterForm.value.dateSort === 'DESC' ? 'ASC' : 'DESC',
		});
	}

	private _initListeners() {
		this._coacheeProfileController.bottomReached$
			.pipe(
				filterNil(),
				filter((bottomReached) => bottomReached.activeTabIndex === 0),
				tap(() =>
					this.store.update((state) => ({
						meetingsOffset: state.meetingsId.length,
					}))
				)
			)
			.subscribe();

		combineLatest([
			this._userApplicationPort.observeSessionUser().pipe(filterNil()),
			this._coacheeProfileController.coachee$.pipe(filterNil()),
			this.select('exportTaskId'),
			this._userTaskExportTaskCommandHandler.commandHandled$,
		])
			.pipe(
				switchMap(([user, coachee, exportTaskId, notification]) =>
					this._userTaskExportTaskQueryHandler.canExportUserMeetings({
						userId: user.id,
						participantId: coachee.id,
						exportTaskId: exportTaskId,
					})
				),
				filterNull(),
				filterError,
				tap((status) => {
					switch (status) {
						case 'DONE':
							if (this.store.getValue().exportTaskId) {
								this._messagingService.publishMessage({
									summaryKey: this.i18n + '.exportDoneToast.summaryKey',
									detailKey: this.i18n + '.exportDoneToast.detailKey',
									severity: 'success',
								});
							}

							this.store.update({ isExporting: false, exportTaskId: null });
							break;
						case 'FAILED':
							if (this.store.getValue().exportTaskId) {
								this._messagingService.publishMessage({
									summaryKey: this.i18n + '.exportFailedToast.summaryKey',
									detailKey: this.i18n + '.exportFailedToast.detailKey',
									severity: 'error',
								});
							}

							this.store.update({ isExporting: false, exportTaskId: null });

							break;
						case 'PENDING':
						case 'IN_PROGRESS':
							this.store.update({ isExporting: true });
							break;
						default:
							this.store.update({ isExporting: false });
							break;
					}
				})
			)
			.subscribe();

		combineLatest([
			this.filterForm.value$,
			this._coacheeProfileController.meetingsChanged$,
		])
			.pipe(
				debounceTime(250),
				tap(() =>
					this.store.update({
						meetingsOffset: 0,
						reloadMeetingsTrigger: Date.now(),
					})
				),
				untilDestroyed(this)
			)
			.subscribe();

		combineLatest([
			this._coacheeProfileController.coachee$.pipe(filterNil()),
			this.select([
				'meetingsOffset',
				'meetingsChunkSize',
				'reloadMeetingsTrigger',
			]),
			this._taskApplicationPort.commandHandled$,
		])
			.pipe(
				switchMap(([coachee, data, notification]) =>
					this._taskApplicationPort
						.searchUserCoachMeetings({
							dueStartTimestamp: this.filterForm.value.date
								? localeDateToDateTime(this.filterForm.value.date, coachee?.timeZone)
										.startOf('day')
										.toMillis()
								: null,
							dueEndTimestamp: this.filterForm.value.date
								? localeDateToDateTime(this.filterForm.value.date, coachee?.timeZone)
										.endOf('day')
										.toMillis()
								: null,
							description: this.filterForm.value.description,
							sortDirection: this.filterForm.value.dateSort || 'DESC',
							statuses: this.filterForm.value.statuses,
							chunkSize: data.meetingsChunkSize,
							offset: data.meetingsOffset,
							sortProperty: 'DUE_DATE_TIME',
							userId: coachee.id,
						})
						.pipe(
							filter((result) => !(result instanceof Error)),
							tap((meetingsId: string[]) =>
								data.meetingsOffset === 0
									? this.store.update({ meetingsId })
									: this.store.update((state) => ({
											meetingsId: uniq([...state.meetingsId, ...meetingsId]),
									  }))
							)
						)
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	public addMeetingsExport() {
		combineLatest([
			this._coacheeProfileController.coachee$.pipe(filterNull()),
			this._userApplicationPort.observeSessionUser().pipe(filterNull()),
		])
			.pipe(
				take(1),
				tap(() => this.store.update({ isExporting: true })),
				filterNull(),
				switchMap(([coachee, authenticatedUser]) => {
					return this._userTaskExportTaskCommandHandler.createUserTaskExportTask({
						userId: authenticatedUser.id,
						exportedTasksType: 'MEETING',
						exportedTasksParticipantId: coachee.id,
					});
				}),
				filterNull(),
				filterError,
				tap((exportTaskId) => this.store.update({ exportTaskId })),
				switchMap((exportTaskId) =>
					this._userTaskExportTaskQueryHandler.observeUserTaskExportTask(
						exportTaskId
					)
				),
				filterNull(),
				switchMap((exportTask) =>
					exportTask.exportedTasksDocumentUrl
						? this._http.get(exportTask.exportedTasksDocumentUrl, {
								responseType: 'blob',
						  })
						: of(null)
				),
				filterNull(),
				tap((blob) =>
					saveAs(
						blob,
						this._translateService.instant(
							this.i18n + '.downloadedExportedDocumentName.text'
						) + '.pdf'
					)
				)
			)
			.subscribe();
	}
}
